// English catalog. Keys are grouped by screen/component. Shared vocabulary lives
// under common.* / chess.* — reuse across screens. Plural entries are objects.
// Kept in sync with sl.ts (same keys). See DESIGN §5.4.

type Entry = string | { one: string; two?: string; few?: string; other: string };

export const en: Record<string, Entry> = {
  "native.signIn": "Sign in to connect ChessME.",
  "native.title": "Connect ChessME for iOS",
  "native.account": "Continue as {name}.",
  "native.intro": "This connects the app to this account so you can continue your Messages games online.",
  "native.continue": "Continue as {name}",
  "native.switch": "Use another account",
  "native.expired": "Sign-in expired. Start again in ChessME.",
  "native.failed": "Sign-in failed.",
  "native.invalid": "Invalid app callback.",
  "native.return": "Return to ChessME",
  "native.restart": "Start again in ChessME.",
  "continuation.loadFailed": "Could not load the offer.",
  "continuation.selected": "Selected Messages position",
  "continuation.history": "Messages move history",
  "continuation.title": "Continue online",
  "continuation.intro": "Continue from this Messages position with no clock and no rating changes.",
  "continuation.copies": "Other Messages copies may remain playable. Agree with your opponent to use the online game from here.",
  "continuation.back": "Back to ChessME",
  "continuation.recoverStorage": "This browser could not recover the saved action. Sign in again before confirming; keep any existing Messages offer for recovery.",
  "continuation.badPosition": "The source position could not be reviewed.",
  "continuation.badHistory": "The source history could not be reviewed.",
  "continuation.start": "Start",
  "continuation.waiting": "Waiting for opponent",
  "continuation.signIn": "Sign in to review and continue",
  "continuation.open": "Open online game",
  "continuation.saved": "The continuation is saved. Opening it keeps the same game and history.",
  "continuation.checking": "Checking saved action…",
  "continuation.check": "Check saved action",
  "continuation.signAgain": "Sign in again",
  "continuation.unknown": "Checking the last action. Its outcome will be recovered before another action can be sent.",
  "continuation.expired": "This offer expired without starting a game. Ask for a new invitation.",
  "continuation.cancelled": "This offer was cancelled.",
  "continuation.share": "Share the invitation, then confirm the account that claims it.",
  "continuation.waitApproval": "Waiting for the inviter to confirm your account.",
  "continuation.withdraw": "Withdraw confirmation",
  "continuation.consent": "I recognize this history and these sides and agree to continue online.",
  "continuation.ready": "Ready to start online",
  "continuation.link": "Invitation link to share with your opponent",
  "continuation.extend": "Extend invitation",
  "continuation.cancel": "Cancel invitation",
  "continuation.existing": "Open existing continuation",
  "continuation.private": "This game is available only to its players. Sign in with the account used for the invitation.",
  "continuation.account": "Use another account",
  "continuation.logoutFailed": "Could not sign out. Try again.",
  "continuation.saveFailed": "The browser could not save this action. Enable site storage before confirming.",
  "continuation.recorded": "The action was recorded. Waiting to retrieve the current offer before another confirmation.",
  "continuation.lostResponse": "The response did not arrive. Checking the saved action keeps the same invitation and game.",
  "continuation.refreshFailed": "Could not refresh the offer. Confirmations wait until its status is available.",
  "continuation.capacity": "All live boards are busy. Both players can confirm again when ready.",
  "continuation.busy": "One of you already has an active game. Finish or pause it, then both confirm again.",
  "continuation.closed": "Starting live games is temporarily paused. Your offer is saved.",
  "continuation.changed": "The offer changed. Review the position and sides again.",
  "continuation.approval": "The inviter must confirm the opponent before you can start.",
  "continuation.sourceConflict": "This Messages game already has an online continuation. Open that game below.",
  "continuation.inviterCancelled": "The inviter cancelled this offer.",
  "continuation.waitReady": "Waiting for the other player to confirm.",
  "continuation.claimed": "Your account is linked to this offer. Review the current details.",
  "continuation.renewLimit": "This offer cannot be extended again. Create a new invitation after it expires.",
  "continuation.failed": "The action could not be completed. Review the current offer before trying again.",
  "continuation.journal": "The saved action needs recovery. Keep this tab open and sign in again.",
  "continuation.legacy": "This older Messages copy was selected for this invitation. Other copies may differ.",
  "continuation.included": "Moves included from Messages: {n}.",
  "continuation.invitedBy": "Invited by {name}, playing {color}.",
  "continuation.signedIn": "Signed in as {name}.",
  "continuation.claimAs": "Continue as {name}",
  "continuation.confirmOpponent": "Confirm {name} as opponent",
  "continuation.confirmSeconds": "Your confirmation lasts {n} seconds. Waiting for the other player.",
  "continuation.past": "Messages history after {n} plies",
  "continuation.context": "Messages allowed {features}. Online play remains unrated.",
  "continuation.undo": "undo",
  "continuation.assistance": "assistance",
  "continuation.and": " and ",
  "continuation.wrongAccount": "This action belongs to {name}. Its status could not be authorized; sign in with the intended account.",
  "game.enterMove": "Enter a move with the keyboard",
  "game.moveNotation": "Move in SAN or coordinate notation",
  "game.playMove": "Play move",
  "game.invalidTypedMove": "Enter a legal move, for example Nf3 or g1f3. Include a promotion piece, such as e8=Q or e7e8q.",
  "game.boardStatus": "{turn} to move. You play {color}. {status}.",
  "game.finished": "Game finished",
  "mode.live": "Play live",
  "mode.messages": "Play in Messages",
  "mode.messagesHelp": "On iPhone or iPad, open ChessME in a Messages conversation to take turns with a friend.",
  "direct.title": "Invite someone to play",
  "direct.body": "Choose the game, then share its link with your opponent. The game starts when they accept.",
  "direct.create": "Create game link",
  "direct.ready": "Your invitation link is ready.",
  "email.title": "Continue with email",
  "email.public": "New here? Your code creates an account. Existing players keep their account and games.",
  "email.existing": "Use the email address on your existing account.",
  "email.address": "Email address",
  "email.sent": "Check {email} for your code.",
  "email.instructions": "If this address is eligible, a code will arrive shortly. It works for five minutes. Use the most recently requested code.",
  "email.resend": "Send a new code",
  "email.resendIn": "New code in {seconds}s",
  "email.change": "Use a different email",
  "email.inviteTitle": "Sign in to review this invitation",
  "email.inviteBody": "Verify your own email address, then return here to choose whether to play.",
  // --- common / chess vocabulary ---
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.ok": "OK",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.loadFailed": "Could not load — try again.",
  "common.home": "Home",
  "common.loading": "Loading…",
  "common.sending": "Sending…",
  "common.saving": "Saving…",
  "common.redirecting": "Redirecting…",
  "common.openingGame": "Opening the game…",
  "common.backHome": "Back home",
  "common.somethingWrong": "Something went wrong.",
  "common.you": "You",
  "chess.white": "White",
  "chess.black": "Black",
  "chess.random": "Random",
  "chess.rated": "rated",
  "chess.casual": "casual",
  "chess.ratedCap": "Rated",
  "chess.blitz": "Blitz",
  "chess.rapid": "Rapid",
  "chess.timeControl": "Time control",
  "chess.yourColor": "Your color",
  "chess.computer": "Computer",
  "plural.games": { one: "{n} game", other: "{n} games" },
  "plural.moves": { one: "{n} move", other: "{n} moves" },
  "plural.members": { one: "{n} member", other: "{n} members" },
  "plural.positions": { one: "{n} position", other: "{n} positions" },

  // --- nav / topnav ---
  "nav.primary": "Primary",
  "nav.play": "Play",
  "nav.players": "Players",
  "nav.games": "Games",
  "nav.study": "Study",
  "nav.activity": "Activity",
  "nav.admin": "Admin",
  "nav.profileTitle": "Profile & settings",

  // --- login + private-club + membership ---
  "login.tagline": "A private online chess club — you play by invitation.",
  "login.loginLabel": "Username or email",
  "login.password": "Password",
  "login.signIn": "Sign in",
  "login.signingIn": "Signing in…",
  "login.needBoth": "Enter your username/email and password.",
  "login.couldNot": "Could not sign in.",
  "login.forgot": "Forgot your password?",
  "login.useCode": "Sign in with a one-time code",
  "login.usePassword": "Use password instead",
  "login.codeProvider": "Send the code through",
  "login.sendCode": "Send sign-in code",
  "login.sendingCode": "Sending a code…",
  "login.codeLabel": "Six-digit code",
  "login.verifyCode": "Verify & sign in",
  "login.codeNeed": "Enter the six-digit code.",
  "login.codeFailed": "The code could not be sent or verified.",
  "login.inviteOnlyLead": "No account yet?",
  "login.inviteOnly": "ChessME is invite-only — ask a member to challenge you, or apply to join.",
  "login.becomeMember": "Become a member",
  "join.title": "Apply to join",
  "join.intro": "ChessME is a small, invitation-only club. Tell us a little about yourself and we'll send you an invitation. Membership is free.",
  "join.name": "Your name",
  "join.email": "Your email",
  "join.emailHelp": "Where we'll send your invitation.",
  "join.experience": "Chess experience",
  "join.experienceOpt": "Optional — e.g. beginner, club player, approx. rating.",
  "join.message": "Anything to add?",
  "join.messageOpt": "Optional — how you heard about us, or who you know in the club.",
  "join.submit": "Send application",
  "join.sending": "Sending…",
  "join.needName": "Please enter your name.",
  "join.needEmail": "Please enter a valid email.",
  "join.success": "Thanks! We've received your application and will email you an invitation soon.",
  "join.failed": "Could not send your application. Please try again.",
  "join.back": "← Back to sign in",

  // --- forgot / reset ---
  "forgot.tagline": "Forgot your password? No problem — we'll email you a reset link.",
  "forgot.label": "Username or email",
  "forgot.submit": "Email me a reset link",
  "forgot.back": "Back to sign in",
  "forgot.need": "Enter your username or email.",
  "forgot.sending": "Sending…",
  "forgot.sent": "If that account exists, a reset link is on its way. Check your email — it expires in an hour.",
  "forgot.failed": "Could not send the link.",
  "reset.tagline": "Choose a new password.",
  "reset.label": "New password",
  "reset.submit": "Set password & sign in",
  "reset.min": "Password must be at least 8 characters.",
  "reset.saving": "Saving…",
  "reset.failed": "Could not reset your password.",

  // --- onboard (invite-accept) ---
  "onboard.headline": "{who} challenges you to a {tc} ({kind}) game.",
  "onboard.aFriend": "A friend",
  "onboard.signupIntro": "Create your account for {email} and jump straight in.",
  "onboard.username": "Username",
  "onboard.password": "Password",
  "onboard.createPlay": "Create account & play",
  "onboard.needUser": "Pick a username of at least 3 characters.",
  "onboard.needPass": "Password must be at least 8 characters.",
  "onboard.creating": "Creating your account…",
  "onboard.signedInAs": "Signed in as {who}.",
  "onboard.acceptPlay": "Accept & play",
  "onboard.decline": "Decline",
  "onboard.notNow": "Not now — back home",
  "onboard.accepting": "Accepting…",
  "onboard.declining": "Declining…",
  "onboard.declined": "Declined — {who} has been let know.",
  "onboard.cantAccept": "Could not accept.",
  "onboard.cantDecline": "Could not decline.",
  "onboard.welcomeBack": "Welcome back, {who} — pop in your password to accept.",
  "onboard.signinPlay": "Sign in & play",
  "onboard.needPassword": "Enter your password.",
  "onboard.signingYouIn": "Signing you in…",
  "onboard.notFoundTitle": "Invite not found",
  "onboard.brokenLink": "This link looks broken.",
  "onboard.acceptedTitle": "Already accepted",
  "onboard.acceptedBody": "This challenge has already been accepted. Sign in to continue.",
  "onboard.expiredTitle": "Invite expired",
  "onboard.expiredBody": "Ask your friend to send a fresh challenge.",
  "onboard.unavailTitle": "Invite unavailable",
  "onboard.unavailBody": "This challenge is no longer open.",

  // --- invite (member → email invite) ---
  "invite.title": "Invite someone new",
  "invite.tagline": "They'll get an email challenge and join straight into the game — no account needed first.",
  "invite.theirEmail": "Their email",
  "invite.ping": "Ping me when they're ready to play",
  "invite.send": "Send challenge",
  "invite.orMember": "← Or challenge a member you already know",
  "invite.needEmail": "Enter your friend's email.",
  "invite.sentTo": "✓ Challenge sent to {email}.",
  "invite.shareLink": "Share the link directly if you like:",
  "invite.failed": "Could not send the invite.",

  // --- home ---
  "home.challengesForYou": "Challenges for you",
  "home.continuePlaying": "Continue playing",
  "home.play": "Play",
  "home.recentResults": "Recent results",
  "home.allGames": "All games →",
  "home.playFriend": "♟ Play a friend",
  "home.playComputer": "🤖 Play computer",
  "home.inviteEmail": "+ Invite someone new by email",
  "home.challengesYou": "{who} challenges you",
  "home.accept": "Accept →",
  "home.incomingMore": "Showing your 20 most recent challenges — accept some to see the rest.",
  "home.vs": "vs {who}",
  "home.resume": "resume",
  "home.recentComputer": "Recent computer games",
  "home.vsComputerL": "vs Computer · L{level}",
  "home.playAgainTitle": "Play {who} again",
  "home.cantChallenge": "Could not send the challenge.",
  "home.paused": "Paused · Open game",
  "result.won": "won",
  "result.lost": "lost",
  "result.draw": "draw",
  "result.aborted": "aborted",
  "result.playing": "playing",

  // --- waiting room ---
  "waiting.brokenLink": "This invite link looks broken.",
  "waiting.expired": "This invitation has expired.",
  "waiting.noLongerOpen": "This challenge is no longer open.",
  "waiting.yourFriend": "your friend",
  "waiting.forOpponent": "Waiting for opponent…",
  "waiting.waiting": "Waiting",
  "waiting.on": "Waiting on {who}",
  "waiting.tagline": "They'll join as soon as they accept. You can leave — the challenge is saved and you'll drop into the game when it starts.",
  "waiting.ready": "I'm ready — nudge them",
  "waiting.leave": "Leave — back home",
  "waiting.nudged": "{who} is online — they've been nudged.",
  "waiting.emailed": "Emailed {who} that you're ready.",
  "waiting.willLet": "We'll let {who} know when we can reach them.",
  "waiting.nudgeFailed": "Couldn't send the nudge.",
  "waiting.expiredPoll": "This invitation expired.",

  // --- players + directory + leaderboard ---
  "players.directory": "Directory",
  "players.leaderboard": "Leaderboard",
  "players.gameOptions": "Game options: ",
  "players.inviteEmail": "+ Invite someone new by email",
  "players.empty": "No other members yet — invite someone to get started.",
  "players.loadFailed": "Could not load the member directory.",
  "players.recentlyPlayed": "Recently played:",
  "players.challengeName": "Challenge {who}",
  "players.online": "online",
  "players.offline": "offline",
  "players.play": "Play",
  "players.unrated": "unrated",
  "players.searchLabel": "Search members",
  "players.searchPlaceholder": "Search members by name…",
  "players.searchEmpty": "No members match “{q}”.",
  "players.onlineNow": "Online now:",
  "players.lbTagline": "Glicko-2 rating per speed. “?” marks a provisional rating.",
  "players.lbFailed": "Could not load the leaderboard.",
  "players.noRated": "No rated {speed} games yet.",

  // --- player profile + settings ---
  "player.loadFailed": "Could not load this player.",
  "player.players": "Players",
  "player.challengeName": "Challenge {who}",
  "player.sendingChal": "Sending challenge…",
  "player.cantChallenge": "Could not send the challenge.",
  "player.cantReach": "You can only challenge members you share a group with. Ask an admin to add you to a group together.",
  "player.h2h": "Head to head",
  "player.h2hEmpty": "You haven't played {who} yet.",
  "player.youWon": "you won",
  "player.drawn": "drawn",
  "player.youLost": "you lost",
  "settings.title": "Settings",
  "settings.boardTheme": "Board theme",
  "settings.appearance": "Appearance",
  "settings.language": "Language",
  "settings.mobile": "Mobile number ",
  "settings.mobileOpt": "(optional, private)",
  "settings.mobileHelp":
    "Unverified contact information only. It is never used as a WhatsApp or Apple Messages route until you link and verify that provider.",
  "settings.analysisDepth": "Analysis depth",
  "settings.analysisDepthHelp":
    "How hard the engine works on the analysis board. “Unlimited” keeps thinking about the position you are looking at, sharpening the evaluation for as long as it stays on screen.",
  "settings.save": "Save",
  "settings.signOut": "Sign out",
  "settings.saving": "Saving…",
  "settings.saved": "Saved.",
  "settings.numberCleared": "Number cleared.",
  "settings.saveFailed": "Could not save.",
  "settings.channels": "Messages and alerts",
  "settings.channelsHelp":
    "Link verified private channels. Your account email remains the dependable fallback.",
  "settings.channelsLoading": "Loading messaging settings…",
  "settings.channelsFailed": "Could not load messaging settings.",
  "settings.alertOrder": "Match alert order",
  "settings.alertOrderHelp":
    "Chess tries these routes in order. A visible in-app alert stops external fallback.",
  "settings.saveAlerts": "Save alert preferences",
  "settings.alertsSaved": "Alert preferences saved.",
  "settings.providerInApp": "In-app",
  "settings.providerEmail": "Email",
  "settings.connected": "Connected",
  "settings.verifiedAddress": "Verified account address: {address}",
  "settings.linkedAs": "Linked and verified as {target}",
  "settings.verifiedIdentity": "verified identity",
  "settings.notLinked": "Available, not linked",
  "settings.notAvailable": "Not configured on this server",
  "settings.link": "Link",
  "settings.unlink": "Unlink",
  "settings.unlinkConfirm": "Unlink {provider} from your Chess account?",
  "settings.codeExpires": "Code expires at {time}.",
  "settings.openTelegram": "Open Telegram",
  "settings.deepLinkHelp": "Opens a private chat with the bot — press Start and you're linked. Nothing to type.",
  "settings.deepLinkFallback": "Telegram not installed? Send {command} to the bot instead.",
  "settings.linkDone": "Linked. Refresh to see it.",
  "settings.mailLink": "Email it to my phone",
  "settings.mailLinkSent": "Sent to {address} — open it on your phone and tap Connect.",
  "connect.preparing": "Preparing your {channel} link…",
  "connect.opening": "Opening {channel}…",
  "connect.open": "Open {channel}",
  "connect.pressStart": "Press Start in the chat and your account is connected.",
  "connect.manual": "Send this to the {channel} bot in a private chat:",
  "connect.unknownChannel": "That channel can't be connected.",
  "connect.failed": "Couldn't prepare the link. Try again from settings.",
  "settings.moveUp": "Move up",
  "settings.moveDown": "Move down",
  "theme.appearance": "Appearance",
  "theme.system": "System",
  "theme.light": "Light",
  "theme.dark": "Dark",
  "theme.systemLabel": "Theme: System",
  "theme.lightLabel": "Theme: Light",
  "theme.darkLabel": "Theme: Dark",
  "board.green": "Green",
  "board.brown": "Brown",
  "board.blue": "Blue",
  "board.pickerLabel": "Board theme",
  "premove.label": "Pre-move",
  "premove.help": "Queue your move during the opponent's turn; it plays automatically after they move.",
  "depth.fast": "Fast",
  "depth.balanced": "Balanced",
  "depth.deep": "Deep",
  "depth.unlimited": "Unlimited",
  "depth.plies": "depth {n}",
  "depth.keepsThinking": "keeps thinking",

  // --- history ---
  "history.title": "Your games",
  "history.exportAll": "Export all (.pgn)",
  "history.timeHorizon": "Time horizon",
  "history.thisWeek": "This week",
  "history.thisMonth": "This month",
  "history.all": "All",
  "history.loadMore": "Load more",
  "history.emptyAll": "No games yet — invite a friend or play the computer.",
  "history.emptyRange": "No games in this range.",
  "history.loadFailed": "Could not load your games.",
  "history.exporting": "Exporting…",
  "history.exportCapped": "Exported your {cap} most recent games — more matched this range but weren't included. Narrow the time horizon to export the rest.",
  "history.exportFailed": "Couldn't export your games — please try again.",
  "history.downloadPgn": "Download PGN",

  // --- activity ---
  "activity.title": "Activity",
  "activity.tagline": "Challenges you've sent, newest first.",
  "activity.empty1": "You haven't sent any challenges yet. ",
  "activity.challengeSomeone": "Challenge someone →",
  "activity.loadFailed": "Could not load your activity.",
  "activity.atBoard": "at the board",
  "activity.joinNow": "Join now",
  "activity.openGame": "Open game",
  "status.pending": "pending",
  "status.accepted": "accepted",
  "status.declined": "declined",
  "status.expired": "expired",
  "status.canceled": "canceled",

  // --- study + collections ---
  "study.importTitle": "Import a game",
  "study.myStudies": "My studies",
  "study.collections": "Collections",
  "study.importHelp": "Paste PGN, choose a .pgn file, or read from your clipboard. Imported games are kept in this browser.",
  "study.import": "Import",
  "study.chooseFile": "Choose file",
  "study.fromClipboard": "From clipboard",
  "study.nothingToImport": "Nothing to import — paste or choose a PGN first.",
  "study.noGamesFound": "No games found in that PGN.",
  "study.imported": { one: "Imported {n} game.", other: "Imported {n} games." },
  "study.skipped": "{n} could not be parsed",
  "study.droppedByCap": "{n} didn't fit (library keeps {max})",
  "study.readFailed": "Could not read that file.",
  "study.clipboardFailed": "Couldn't read the clipboard — paste manually instead.",
  "study.saveFailed": "The browser could not save these games. Nothing was imported; your PGN is still here.",
  "study.emptyStudies": "No studies yet — import a game above to start.",
  "study.delete": "Delete",
  "openings.title": "Openings",
  "openings.panelHelp": "Train 30 mainstream openings from either side: guided courses with coaching, plus compact supported theory.",
  "openings.openGames": "Open games — 1.e4 e5",
  "openings.openGamesHelp": "Both sides stake the centre from move one",
  "openings.semiOpenGames": "Defences to 1.e4",
  "openings.semiOpenGamesHelp": "Counter-attacking answers: 1…c5, 1…c6, 1…e6, 1…d5",
  "openings.closedFlank": "Queen's pawn & flank — 1.d4, 1.c4, 1.Nf3",
  "openings.closedFlankHelp": "Closed systems and today's young-elite favourites",
  "openings.start": "Start",
  "openings.trainAs": "Train as",
  "openings.trainingWhite": "Training as White.",
  "openings.trainingBlack": "Training as Black.",
  "openings.chooseSide": "Choose the side to train",
  "openings.yourMove": "Your move — find a supported continuation.",
  "openings.yourMoveMany": "Your move — {n} reviewed continuations here; any of them advances the lesson.",
  "openings.hint": "Hint",
  "openings.showMove": "Show me",
  "openings.showSupported": "Show moves",
  "openings.supportedMoves": "Supported moves",
  "openings.supportedHelp": "Supported continuations are now shown as buttons and gray arrows. Choose one; no move has been made for you.",
  "openings.supported": "supported",
  "openings.supportedOpening": "supported opening",
  "openings.supportedContinuation": "An established continuation; detailed coaching is not available for this move yet.",
  "openings.wrong": "Not quite — try again.",
  "openings.notCovered": "{move} is legal, but it is not in the curated supported set here. Try again, or show the supported moves.",
  "openings.expectedMove": "The target move is {move}.",
  "openings.mainLine": "Main line:",
  "openings.newTargetLine": "A new uncovered line is the target.",
  "openings.resumeFromFork": "Continue with another uncovered line from the nearest shared branch point.",
  "openings.linesCovered": "{n}/{total} lines covered",
  "openings.lineCompleteTitle": "Line complete",
  "openings.courseCompleteTitle": "Course complete",
  "openings.lineCompleted": "This line is covered. Choose Next branch to keep building coverage.",
  "openings.courseCompleted": "Every supported line is now covered. Choose Next branch to revisit another.",
  "openings.continue": "The line continues.",
  "openings.nextLine": "Next branch",
  "openings.replay": "Replay",
  "openings.choiceRank": "choice {n} of {total}",
  "openings.switchedVariation": "You switched variations — this branch is now the target.",
  "openings.opponentReply": "opponent reply",
  "openings.candidateMoves": "Candidate moves",
  "openings.candidateHelp": "One or many curated continuations can appear here. Choose a button or make the move on the board.",
  "openings.candidateHelpBook": "A dashed move enters another supported opening; it does not add coverage to this course.",
  "openings.bookChoice": "book exploration",
  "openings.bookCredit": "Theory names and branches:",
  "openings.bookAlternativeHelp": "This enters {opening}, a supported branch outside this course. No coverage is awarded here; take back or open its study.",
  "openings.takeBack": "Take back",
  "openings.backInCourse": "Back in this course. Find a supported continuation, or reveal the available moves.",
  "openings.studyOpening": "Study {opening}",
  "openings.tier.guided": "Guided",
  "openings.tier.supported": "Supported",
  "openings.localProgress": "Opening coverage is kept in this browser and does not follow your account to another device.",
  "openings.role.primary": "main line",
  "openings.role.alternative": "reviewed alternative",
  "openings.role.retry": "retry move",
  "openings.verdict.best": "main move",
  "openings.verdict.good": "sound alternative",
  "openings.verdict.playable": "playable book move",
  "openings.verdict.mistake": "instructive mistake",
  "openings.verdict.unreviewed": "not covered here",
  "openings.lineUnavailable": "This course line could not be replayed.",
  "openings.playVsComputer": "vs Computer",
  "openings.restart": "Restart",
  "openings.backToStudy": "Back to Study",
  "openings.unknownCourse": "That course doesn't exist.",
  "collections.classicGames": "Classic games",
  "collections.classicGamesHelp": "Public-domain brilliancies from the romantic era",
  "collections.modernGames": "Modern games",
  "collections.modernGamesHelp": "Celebrated games from the last few decades",
  "collections.none": "No collections available.",
  "collections.notAvail": "That collection isn't available.",
  "collections.notInColl": "That game isn't in the collection.",
  "collections.studyBack": "← Study",
  "collections.emptyColl": "This collection has no games.",
  "collections.backToStudy": "Back to Study",

  // --- computer (play the bot) ---
  "computer.title": "Play the computer",
  "computer.strength": "Strength",
  "computer.level": "Level",
  "computer.timeControl": "Time control",
  "computer.noClock": "No clock",
  "computer.playAs": "Play as",
  "computer.startGame": "Start game",
  "computer.newGame": "New game",
  "computer.resign": "Resign",
  "computer.resignConfirm": "Resign this game?",
  "computer.you": "You",
  "computer.loadingEngine": "Loading engine…",
  "computer.positionReadyLoadingEngine": "Position ready · loading engine…",
  "computer.engineFailed": "Could not load the engine.",
  "levels.beginner": "beginner",
  "levels.club": "club",
  "levels.strong": "strong",
  "levels.maximum": "maximum",
  "levels.levelN": "Level {n}",
  "levels.levelNq": "Level {n} · {q}",

  // --- game (live game) ---
  "game.loadFailed": "Could not load the game.",
  "game.offerDraw": "Offer draw",
  "game.resign": "Resign",
  "game.resignConfirm": "Resign this game?",
  "game.saySomething": "Say something…",
  "game.spectating": "Spectating — you are not a player in this game.",
  "game.online": "Online",
  "game.awayReconnect": "Away — reconnecting…",
  "game.offline": "Offline",
  "game.connection": "Connection",
  "game.drawOffered": "Opponent offers a draw. ",
  "game.accept": "Accept",
  "game.decline": "Decline",
  "game.drawWaiting": "Draw offered — waiting for opponent.",
  "game.rematchWaiting": "Rematch offered — waiting for opponent…",
  "game.rematchWants": "Opponent wants a rematch. ",
  "game.acceptRematch": "Accept rematch",
  "game.rematchReady": "Rematch ready. ",
  "game.join": "Join",
  "game.oppLeftClaim": "Opponent left — claim the win in {n}s… ",
  "game.oppLeft": "Opponent left. ",
  "game.claimWin": "Claim win",
  "game.live": "Live",
  "game.reconnecting": "Reconnecting…",
  "game.analyze": "Analyze",
  "game.rematch": "Rematch",
  "game.challengeAgain": "Challenge again",
  "result.youWon": "You won",
  "result.youLost": "You lost",
  "result.drawWord": "Draw",
  "result.wins": "{who} wins",
  "reason.checkmate": "by checkmate",
  "reason.resignation": "by resignation",
  "reason.onTime": "on time",
  "reason.abandoned": "abandoned",

  // --- analysis board ---
  "analysis.copyPgn": "Copy PGN",
  "analysis.downloadPgn": "Download .pgn",
  "analysis.copyFen": "Copy FEN",
  "analysis.copied": "Copied ✓",
  "analysis.copyFailed": "Copy failed",
  "analysis.saved": "Saved ✓",
  "analysis.startingEngine": "Starting engine…",
  "analysis.engineUnavailable": "Engine unavailable.",
  "analysis.done": "Analysis complete · depth {depth}",
  "analysis.thinking": "Thinking… depth {depth}",
  "analysis.bestLine": "Best line · depth {depth}",
  "analysis.positions": "{done}/{total} positions",
  "analysis.gameInProgress": "Game in progress",
  "analysis.botGameGone": "That computer game is no longer in this browser's history.",
  "analysis.studyGone": "That study is no longer in this browser's library.",
  "analysis.discardLine": "Discard line",
  "analysis.backToGame": "Back to game",
  "analysis.bestWas": "Best was {move}",

  // --- review nav (transport controls) ---
  "review.start": "Go to start",
  "review.startT": "Start (↑)",
  "review.prev": "Previous move",
  "review.prevT": "Back (←)",
  "review.next": "Next move",
  "review.nextT": "Forward (→)",
  "review.end": "Go to end",
  "review.endT": "End (↓)",
  "review.flip": "Flip board",

  // --- shared components ---
  "qc.timeControl": "Time control",
  "qc.yourColor": "Your color",
  "qc.rated": "Rated",
  "promo.promoteTo": "Promote to {role}",
  "promo.queen": "queen",
  "promo.rook": "rook",
  "promo.bishop": "bishop",
  "promo.knight": "knight",
  "promo.cancel": "Cancel",
  "mute.unmute": "Unmute sounds",
  "mute.mute": "Mute sounds",
  "mute.soundOff": "Sound off",
  "mute.soundOn": "Sound on",
  "turnAlerts.label": "Games and challenges waiting for you",
  "turnAlerts.yourMove": "your move",
  "turnAlerts.waiting": "waiting",
  "ready.challengesYou": "{who} challenges you to a game.",
  "ready.rematch": "{who} wants a rematch.",
  "ready.accepted": "{who} accepted — your game is ready.",
  "ready.waitingForYou": "{who} is waiting for you.",
  "ready.readyToPlay": "{who} is ready to play.",
  "ready.accept": "Accept",
  "ready.join": "Join",
  "ready.play": "Play",
  "ready.wait": "Wait — one moment",
  "ready.later": "Play later",
  "ready.note": "One moment — I'll be right there.",
  "ready.askedWait": "asked you to wait",
  "ready.declinedChal": "declined your challenge",
  "ready.wantsLater": "wants to play later",
  "ready.dismiss": "Dismiss",
  "serverStatus.lost": "Connection lost — trying to reconnect…",

  // --- PGN result/termination lines ---
  "term.checkmate": "Checkmate",
  "term.resignation": "Resignation",
  "term.timeout": "Time out",
  "term.stalemate": "Stalemate",
  "term.insufficient": "Insufficient material",
  "term.repetition": "Threefold repetition",
  "term.repetitionShort": "Repetition",
  "term.drawClaimed": "Draw claimed",
  "term.fiftyMove": "Fifty-move rule",
  "term.draw": "Draw",
  "term.drawAgreed": "Draw agreed",
  "term.abandonment": "Abandonment",
  "pgn.whiteWins": "White wins",
  "pgn.blackWins": "Black wins",
  "pgn.draw": "Draw",
  "pgn.game": "Game",

  // --- admin ---
  "admin.tabMembers": "Members",
  "admin.tabGroups": "Groups",
  "admin.tabMessaging": "Messaging",
  "admin.tabDanger": "Danger zone",
  "admin.messagingTagline": "Provider health, durable delivery queue and independent circuit breakers.",
  "admin.messagingRefresh": "Refresh",
  "admin.messagingLoadFailed": "Could not load messaging status.",
  "admin.messagingQueueDepth": "queued",
  "admin.messagingOldest": "oldest age",
  "admin.messagingDead": "dead / review",
  "admin.messagingAge": "{seconds}s",
  "admin.messagingAlerts": "{count} operational alert(s) need attention.",
  "admin.messagingProviders": "Providers",
  "admin.messagingProvider": "Provider / account",
  "admin.messagingState": "State",
  "admin.messagingLastSuccess": "Last success",
  "admin.messagingLastError": "Last error class",
  "admin.messagingEnable": "Enable",
  "admin.messagingDisable": "Disable",
  "admin.messagingDisableQ": "Disable {provider}/{account}? New routes and inbound processing will stop; queued work stays visible.",
  "admin.messagingControlFailed": "Could not change the provider circuit breaker.",
  "admin.messagingNoProviders": "No providers are configured.",
  "admin.messagingQueued": "Queued notifications",
  "admin.messagingDeadLetters": "Dead letters and ambiguous sends",
  "admin.messagingKind": "Kind",
  "admin.messagingAttempts": "Attempts",
  "admin.messagingNoneQueued": "The queue is empty.",
  "admin.messagingNoneDead": "No dead letters or ambiguous sends.",
  "admin.messagingRetry": "Retry",
  "admin.messagingRetryQ": "Retry dead letter {id}? This is allowed only for non-secret, unambiguous work.",
  "admin.messagingRetryFailed": "Could not retry this dead letter.",
  "admin.membersTagline": "Everyone on the site. Roles, bans and deletion.",
  "admin.searchLabel": "Search members",
  "admin.searchPlaceholder": "Search by name or email…",
  "admin.allRoles": "All roles",
  "admin.roleAdmins": "Admins",
  "admin.rolePlayers": "Players",
  "admin.roleSuperuser": "Superuser",
  "admin.anyStatus": "Any status",
  "admin.statusActive": "Active",
  "admin.statusBanned": "Banned",
  "admin.thMember": "Member",
  "admin.thRole": "Role",
  "admin.thStatus": "Status",
  "admin.thInvitedBy": "Invited by",
  "admin.thActions": "Actions",
  "admin.roleAdmin": "admin",
  "admin.rolePlayer": "player",
  "admin.roleSuper": "superuser",
  "admin.badgeBanned": "banned",
  "admin.badgeActive": "active",
  "admin.makeAdmin": "Make admin",
  "admin.makePlayer": "Make player",
  "admin.ban": "Ban",
  "admin.unban": "Unban",
  "admin.delete": "Delete",
  "admin.deleteUserQ": "Permanently delete {name}? This can't be undone.",
  "admin.actionFailed": "Action failed.",
  "admin.noMatchFilters": "No members match these filters.",
  "admin.groupsTagline": "Groups decide who a player may challenge — the union of the groups they belong to.",
  "admin.groupsFailed": "Could not load groups.",
  "admin.newGroup": "+ New group",
  "admin.name": "Name",
  "admin.namePlaceholder": "e.g. Tuesday club",
  "admin.kind": "Kind",
  "admin.kindStatic": "Static (hand-picked roster)",
  "admin.kindLive": "Live (query)",
  "admin.kindBadgeStatic": "static",
  "admin.kindBadgeLive": "live",
  "admin.rule": "Rule",
  "admin.ruleDistance": "Invite distance ≤ N",
  "admin.ruleRating": "Rating range",
  "admin.maxHops": "Max hops",
  "admin.speed": "Speed",
  "admin.min": "Min",
  "admin.max": "Max",
  "admin.createGroup": "Create group",
  "admin.creating": "Creating…",
  "admin.created": "Created.",
  "admin.nameNeeded": "Give the group a name.",
  "admin.createFailed": "Could not create the group.",
  "admin.members": "Members",
  "admin.handPicked": "hand-picked roster",
  "admin.deleteGroupQ": "Delete the group \"{name}\"?",
  "admin.deleteFailed": "Could not delete the group.",
  "admin.loadingMembers": "Loading members…",
  "admin.membersFailed": "Could not load members.",
  "admin.noMembers": "No members yet.",
  "admin.relativeNote": "Live rule — membership is computed per player. Showing members relative to you:",
  "admin.addMember": "Add a member — type a name or email…",
  "admin.addMemberLabel": "Add a member",
  "admin.noMatches": "No matching members.",
  "admin.addFailed": "Could not add member.",
  "admin.remove": "Remove {name}",
  "admin.delegation": "Delegation",
  "admin.delegationHelp": "Allow admins (not just you) to grant and revoke the admin role.",
  "admin.delegationOn": "Admins may now delegate.",
  "admin.delegationOff": "Delegation disabled.",
  "admin.resetDb": "Reset database",
  "admin.resetHelp": "Wipes every game, challenge, rating, group and member — <strong>keeps only you</strong>. For dev only; it can't be undone.",
  "admin.resetPlaceholder": "type RESET to enable",
  "admin.wipeQ": "Really wipe the database? This deletes every game, user and invite.",
  "admin.resetting": "Resetting…",
  "admin.resetDone": "Database reset. Reloading…",
  "admin.resetFailed": "Reset failed.",
  "admin.membership": "Membership",
  "admin.membershipOpen": "Accept applications to join (shows “Become a member” on the login page).",
  "admin.membershipNotify": "Send applications to",
  "admin.applicationsOpen": "Applications are open.",
  "admin.applicationsClosed": "Applications are closed.",
  "game.unavailable": "Game temporarily unavailable. Your last saved position is shown.",
  "game.withdrawConfirmation": "Withdraw confirmation",
  "game.playedInMessages": "Played in Messages",
  "game.onlineBoundary": "Online play begins here",
  "game.pausedInterruption": "Game paused after an interruption. Both players can resume when ready.",
  "game.pausedConsent": "Game paused. Both players can resume when ready.",
  "game.savedClocks": "Clocks use the last saved remaining times.",
  "game.readyExpires": "Your confirmation expires in {seconds} seconds.",
  "game.firstMoveDeadline": "Play the first move within {seconds} seconds.",
  "game.untimedAbsence": "Untimed · Unrated. The game pauses when either player leaves for two minutes.",
  "game.awayCountdown": "{side} appears away. The game pauses in {seconds} seconds unless both players return.",
  "game.importedCount": "{count} half-moves were played in Messages.",
  "game.unfinished": "Game did not finish",
  "game.untimedTerms": "Untimed · Unrated",
  "game.pauseGame": "Pause game",
  "game.resumeReady": "Ready to resume",
  "command.stale": "The game changed. Review the current position and try again.",
  "command.illegal": "That move is not legal in this position.",
  "command.capacity": "All live boards are busy. Try again shortly. Both players must confirm again.",
  "command.busy": "One of the players already has an active game. Both players must confirm again.",
  "command.closed": "New live games are temporarily paused. Try again later.",
  "command.rematch": "The rematch is ready. Open the recorded game.",
  "command.timeControl": "Choose a new time control to play again.",
  "command.waiting": "Waiting for the other player to confirm.",
  "command.finished": "This game has finished.",
  "command.paused": "Game paused. Both players can resume when ready.",
  "command.forbidden": "This account cannot change this game.",
  "command.turn": "It is the other player's turn.",
  "command.time": "The other player still has time.",
  "command.claim": "A win cannot be claimed yet.",
  "command.wait": "Please wait before trying again.",
  "command.drawPending": "Your draw offer is waiting for a reply.",
  "command.noDraw": "There is no pending draw offer.",
  "command.failed": "The action could not be completed. Review the current game and try again.",
};
